import {
  ArrowDown,
  ArrowRight,
  Check,
  ChevronRight,
  Flame,
  Gauge,
  MessageCircle,
  PackageCheck,
  Ruler,
  ShieldCheck,
  Snowflake,
  Sparkles,
  Thermometer,
  UtensilsCrossed,
  Waves,
  Zap,
} from "lucide-react";
import { primaryWA, waLink } from "@/lib/site";
import { trackEvent } from "@/lib/analytics";

const ASSET = "/images/kitchen-equipment";

const products = [
  {
    name: "Bubbletank Dishwasher",
    type: "Washing system",
    image: "bubbletank-dishwasher.png",
    feature: "Cuci sayur, buah, piring & foodtray",
    specs: ["Unit 130 × 100 × 75 cm", "Tangki 110 × 80 × 32 cm", "5.5 kW · 220 V & 380 V", "Kapasitas air 230 L"],
    icon: Waves,
    tone: "blue",
  },
  {
    name: "Super Dishwasher",
    type: "Washing system",
    image: "super-dishwasher.png",
    feature: "Mencuci higienis sekaligus mengeringkan",
    specs: ["Kapasitas 3.000 disc/jam", "Dimensi 328 × 87 × 188 cm", "Bodi stainless steel", "Untuk operasional skala besar"],
    icon: Sparkles,
    tone: "red",
  },
  {
    name: "Mesin Pengering Foodtray (1 pintu)",
    type: "Drying system",
    image: "foottray-dryer-single.png",
    feature: "Pengeringan foodtray yang aman & merata",
    specs: ["High temperature hot air", "100% stainless steel", "Interior mudah dibersihkan", "Rak dapat dilepas"],
    icon: Zap,
    tone: "gold",
  },
  {
    name: "Mesin Pengering Foodtray (2 pintu)",
    type: "Drying system",
    image: "foottray-dryer-double.png",
    feature: "Pengering foodtray listrik berkapasitas besar",
    specs: ["Sirkulasi udara panas", "Stainless steel premium", "Higienis & mudah dibersihkan", "Pilihan model double door"],
    icon: Thermometer,
    tone: "gold",
  },
  {
    name: "Kwali Range 1.8M",
    type: "Cooking system",
    image: "kuali-range-2-burner.png",
    feature: "Range stainless steel untuk proses memasak skala produksi",
    specs: ["Dua area burner", "Bodi stainless steel", "Panel kontrol terpisah", "Untuk dapur komersial"],
    icon: Flame,
    tone: "gold",
  },
  {
    name: "Stockpot Premium",
    type: "Cooking system",
    image: "stockpot-brochure.png",
    feature: "Kuat, efisien, tahan lama",
    specs: ["Material body stainless steel", "Low pressure type", "Electronic manual ignition", "Dimensi 650 × 650 × 600 mm"],
    icon: Flame,
    tone: "red",
  },
  {
    name: "Rice Steamer 12 Tray",
    type: "Steaming system",
    image: "rice-steamer.jpeg",
    feature: "Perfect steam. Perfect rice. Every time.",
    specs: ["12 tray stainless steel 304", "Dimensi 700 × 650 × 1670 mm", "Distribusi uap merata", "Hygienic & easy to clean"],
    icon: UtensilsCrossed,
    tone: "gold",
  },
  {
    name: "Rice Steamer Tray",
    type: "Food service",
    image: "steamer-tray.png",
    feature: "Tray stainless steel untuk kebutuhan rice steamer",
    specs: ["Material stainless steel", "Bentuk tray datar", "Mudah dibersihkan", "Untuk operasional food service"],
    icon: PackageCheck,
    tone: "blue",
  },
  {
    name: "Standard Foodtray BGN",
    type: "Food service",
    image: "foodtray-bgn-full.png",
    feature: "Lolos uji tes cairan · SUS 304 & magnet",
    specs: ["Piring 28 × 22 × 4.5 cm", "Tutup 29 × 22.8 × 2.5 cm", "Berat 470 gr", "Stainless steel SUS 304"],
    icon: PackageCheck,
    tone: "blue",
  },
  {
    name: "Keranjang Serbaguna",
    type: "Food service",
    image: "industrial-basket.png",
    feature: "Lega betul & kuat betul",
    specs: ["Dimensi 60 × 42 × 32.5 cm", "Tampung banyak", "Kuat & tahan lama", "Desain industri praktis"],
    icon: PackageCheck,
    tone: "red",
  },
  {
    name: "Cold Storage AMEX",
    type: "Cold chain",
    image: "cold-storage-brochure.png",
    feature: "Built for absolute freshness",
    specs: ["Suhu 10°C hingga -20°C", "Nominal power 3 HP", "High density polyurethane", "Dimensi 3.5 × 2.2 × 2.2 m"],
    icon: Snowflake,
    tone: "blue",
  },
];

const operatingPoints = [
  { icon: ShieldCheck, title: "Food-grade material", text: "Pilihan produk stainless steel untuk kebutuhan dapur profesional." },
  { icon: Gauge, title: "Ready for volume", text: "Dirancang untuk mendukung kapasitas produksi dan layanan besar." },
  { icon: Ruler, title: "Specification-led", text: "Dimensi dan kemampuan utama ditampilkan agar mudah disesuaikan." },
];

const ProductCard = ({ product, index }) => {
  const Icon = product.icon;
  return (
    <article className={`kitchen-card kitchen-card--${product.tone}`}>
      <div className="kitchen-card__visual">
        <div className="kitchen-card__serial">0{index + 1}</div>
        <div className="kitchen-card__category"><Icon className="w-3.5 h-3.5" /> {product.type}</div>
        <img className={product.imageFit ? `kitchen-card__image--${product.imageFit}` : ""} src={`${ASSET}/${product.image}`} alt={product.name} loading="lazy" />
      </div>
      <div className="kitchen-card__content p-5 sm:p-6">
        <h3 className="font-display text-xl sm:text-2xl font-semibold text-animax-black">{product.name}</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-600">{product.feature}</p>
        <ul className="mt-5 grid gap-2.5">
          {product.specs.map((spec) => <li key={spec} className="flex items-start gap-2.5 text-xs sm:text-sm text-neutral-700"><Check className="w-4 h-4 mt-0.5 shrink-0 text-animax-orange" />{spec}</li>)}
        </ul>
        <a
          href={waLink(primaryWA.raw, `Halo ${primaryWA.name}, saya ingin mengetahui spesifikasi ${product.name}.`)}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackEvent("click_tanya_harga", { category: "product", label: product.name, product_name: "Kitchen Equipment" })}
          className="kitchen-card__link"
        >Tanya spesifikasi <ArrowRight className="w-4 h-4" /></a>
      </div>
    </article>
  );
};

export const KitchenEquipmentPage = () => (
  <main className="kitchen-page" data-testid="kitchen-equipment-page">
    <section className="kitchen-hero" id="kitchen-equipment">
      <div
        className="kitchen-hero__photo"
        aria-hidden="true"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/kitchen-equipment/kitchen-hero-background.png)` }}
      />
      <div className="kitchen-hero__grid" />
      <div className="kitchen-hero__scale">
        <div className="kitchen-orbit kitchen-orbit--one" /><div className="kitchen-orbit kitchen-orbit--two" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 relative pt-32 sm:pt-40 pb-14 sm:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-5 items-center">
            <div className="lg:col-span-7">
              <div className="kitchen-eyebrow"><span>AMEX</span> Commercial Kitchen Equipment</div>
              <h1 className="kitchen-hero__title kitchen-hero__title--single">Power your professional<br /><em>Kitchen</em></h1>
              <p className="kitchen-hero__lead">Rangkaian peralatan dapur komersial AMEX untuk proses memasak, mencuci, mengeringkan, menyimpan, dan menyajikan makanan dalam skala profesional.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#katalog-kitchen" className="kitchen-button kitchen-button--solid"><PackageCheck className="w-4 h-4" /> Jelajahi 11 Produk <ArrowDown className="w-4 h-4" /></a>
                <a href={waLink(primaryWA.raw, "Halo Ken, saya ingin konsultasi kebutuhan Kitchen Equipment AMEX.")} target="_blank" rel="noreferrer" className="kitchen-button kitchen-button--outline"><MessageCircle className="w-4 h-4" /> Konsultasi Kebutuhan</a>
              </div>
              <div className="kitchen-hero__stats mt-10 grid grid-cols-3 gap-3 max-w-xl">
                <div className="kitchen-stat"><strong>11</strong><span>Produk<br />unggulan</span></div>
                <div className="kitchen-stat"><strong>304</strong><span>Stainless<br />steel series</span></div>
                <div className="kitchen-stat"><strong>SPPG</strong><span>Ready for<br />operation</span></div>
              </div>
            </div>
            <div className="lg:col-span-5 kitchen-hero__product kitchen-hero__product--cold">
              <div className="kitchen-hero__glow" />
              <div className="kitchen-hero__brochure">
                <img src={`${ASSET}/cold-storage-brochure.png`} alt="AMEX Cold Storage" />
                <div className="kitchen-spec-chip kitchen-spec-chip--cold"><span>Cold<br />Storage</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 grid md:grid-cols-3 gap-4">
        {operatingPoints.map(({ icon: Icon, title, text }) => <div key={title} className="kitchen-point"><div className="kitchen-point__icon"><Icon className="w-5 h-5" /></div><div><h2>{title}</h2><p>{text}</p></div></div>)}
      </div>
    </section>

    <section className="kitchen-spotlight">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="kitchen-spotlight__visual"><div className="kitchen-corner">AMEX</div><img src={`${ASSET}/super-dishwasher-brochure.png`} alt="AMEX Super Dishwasher" /></div>
          <div>
            <div className="kitchen-section-label">Spotlight system / 01</div>
            <h2 className="kitchen-section-title">A clean flow starts with a reliable wash.</h2>
            <p className="kitchen-section-copy">Dari bubbletank untuk mencuci sayur, buah, piring, hingga foodtray, sampai Super Dishwasher untuk operasional berkapasitas tinggi—pilih sistem yang sesuai alur dapur Anda.</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              <div className="kitchen-mini-spec"><Waves /><span><b>360° cleaning</b>Putaran cuci menyeluruh</span></div>
              <div className="kitchen-mini-spec"><Zap /><span><b>5.5 kW power</b>Pilihan listrik 220 V / 380 V</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="katalog-kitchen" className="bg-[#f7f8fa] py-16 sm:py-24" data-testid="kitchen-product-catalog">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <div className="max-w-3xl"><div className="kitchen-section-label">AMEX Product Index</div><h2 className="kitchen-section-title text-animax-black">Built for every station in your kitchen.</h2><p className="kitchen-section-copy">Spesifikasi berikut dirangkum dari e-brochure yang Anda berikan. Hubungi tim kami untuk ketersediaan, konfigurasi, dan penawaran.</p><p className="kitchen-catalog-note">Gambar hanyalah ilustrasi. Untuk detail produk, silakan langsung kontak tim kami.</p></div>
        <div className="kitchen-product-grid mt-10 sm:mt-14 grid md:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6 auto-rows-fr">
          {products.map((product, index) => <ProductCard key={product.name} product={product} index={index} />)}
          <article className="kitchen-more-card">
            <div className="kitchen-more-card__number">+</div>
            <div className="kitchen-section-label">Need something else?</div>
            <h3>Kitchen Equipment Lainnya</h3>
            <p>Belum menemukan peralatan yang Anda cari? Konsultasikan kebutuhan dapur komersial Anda bersama Pak Ken.</p>
            <a href={waLink(primaryWA.raw, "Halo Pak Ken, saya ingin menanyakan Kitchen Equipment lainnya.")} target="_blank" rel="noreferrer" onClick={() => trackEvent("click_tanya_harga", { category: "product", label: "Kitchen Equipment Lainnya", product_name: "Kitchen Equipment" })}><MessageCircle className="w-4 h-4" /> Hubungi Tim Kami</a>
          </article>
        </div>
      </div>
    </section>

    <section className="kitchen-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
        <div><div className="kitchen-section-label text-animax-yellow">Build your kitchen line</div><h2>Siap merancang<br />kebutuhan dapur Anda?</h2><p>Konsultasikan alur kerja, kapasitas, dan produk yang paling pas bersama tim AMEX.</p></div>
        <a href={waLink(primaryWA.raw, "Halo Ken, saya ingin konsultasi Kitchen Equipment AMEX.")} target="_blank" rel="noreferrer" className="kitchen-button kitchen-button--yellow">Hubungi Ken <ChevronRight className="w-5 h-5" /></a>
      </div>
    </section>
  </main>
);
